import {isAutomatedReviewBot, reviewerDisplayName} from '../ReviewerIdentity'
import {DeferredType} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import type {
  CompletedReview,
  CopilotReviewerRow,
  PendingReviewRequest,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {isCopilotReviewer} from '../copilot-reviewer'
import {CompletedReviewRow} from './CompletedReviewRow'
import {CopilotRequestRow} from './CopilotRequestRow'
import {DeferredCopilotReviewRow} from './DeferredCopilotReviewRow'
import {PendingReviewRow} from './PendingReviewRow'

export function CopilotRow({
  copilotPendingRequest,
  copilotCompletedReview,
  copilotReviewerRow,
  awaitingInitialCopilotRequest,
  clearMutationError,
  copilotRequestMutationError,
  reviewerMutationError,
  reviewerMutationSuccess,
  restoreFocus,
  mutationSuccess,
}: {
  copilotPendingRequest: PendingReviewRequest | undefined
  copilotCompletedReview: CompletedReview | undefined
  copilotReviewerRow: CopilotReviewerRow | undefined
  awaitingInitialCopilotRequest: boolean
  clearMutationError: () => void
  copilotRequestMutationError: (origin: Element | null) => void
  reviewerMutationError: (reviewerName: string | undefined) => (origin: Element | null) => void
  reviewerMutationSuccess: (reviewerName: string | undefined) => () => void
  restoreFocus: () => void
  mutationSuccess: (message: string) => void
}) {
  if (copilotPendingRequest && copilotReviewerRow) {
    const reviewerName = copilotPendingRequest.reviewer
      ? reviewerDisplayName(copilotPendingRequest.reviewer)
      : 'Copilot'
    if (copilotPendingRequest.deferredType === DeferredType.COPILOT) {
      return (
        <DeferredCopilotReviewRow
          key={`pending-${copilotPendingRequest.reviewer?.id}`}
          request={copilotPendingRequest}
          row={copilotReviewerRow}
          onMutationStart={clearMutationError}
          onMutationError={copilotRequestMutationError}
          onMutationSuccess={() => mutationSuccess(`Review requested from ${reviewerName}`)}
          onCloseWithoutTarget={restoreFocus}
        />
      )
    }
    return (
      <PendingReviewRow
        key={`pending-${copilotPendingRequest.reviewer?.id}`}
        request={copilotPendingRequest}
        copilotReviewerRow={copilotReviewerRow}
        lastCompletedCopilotReviewEffort={copilotReviewerRow.lastExecutedReviewEffort}
      />
    )
  }
  if (copilotCompletedReview) {
    const reviewerName = copilotCompletedReview.reviewer
      ? reviewerDisplayName(copilotCompletedReview.reviewer)
      : 'Copilot'
    return (
      <CompletedReviewRow
        key={`completed-${copilotCompletedReview.id}`}
        review={copilotCompletedReview}
        muted={
          isCopilotReviewer(copilotCompletedReview.reviewer) || isAutomatedReviewBot(copilotCompletedReview.reviewer)
        }
        copilotReviewerRow={copilotReviewerRow}
        onMutationStart={clearMutationError}
        onMutationError={reviewerMutationError(reviewerName)}
        onMutationSuccess={reviewerMutationSuccess(reviewerName)}
        onCloseWithoutTarget={restoreFocus}
      />
    )
  }
  if (awaitingInitialCopilotRequest && copilotReviewerRow) {
    return (
      <CopilotRequestRow
        row={copilotReviewerRow}
        onMutationStart={clearMutationError}
        onMutationError={copilotRequestMutationError}
        onMutationSuccess={() =>
          mutationSuccess(
            `Review requested from ${copilotReviewerRow.reviewer ? reviewerDisplayName(copilotReviewerRow.reviewer) : 'Copilot'}`,
          )
        }
        onCloseWithoutTarget={restoreFocus}
      />
    )
  }
  return null
}
